import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/app/hooks'
import { useCanAccess, useCanPlatform } from '@/features/auth/access'
import { tokenStorage } from '@/lib/token-storage'

/**
 * Gate for authenticated routes. Redirects to /login if not authenticated.
 */
export function RequireAuth() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const user = useAppSelector((s) => s.auth.user)
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  // Platform accounts (super_admin / platform_staff) have no company workspace in
  // the tenant portal — tenant-scoped pages/endpoints would 404/422. They belong in
  // the separate admin console. Impersonation sessions (acting within a tenant) pass through.
  const isPlatform = user?.user_type === 'super_admin' || user?.user_type === 'platform_staff'
  if (isPlatform && !tokenStorage.isImpersonating()) {
    return <PlatformAccountNotice />
  }
  return <Outlet />
}

function PlatformAccountNotice() {
  const adminUrl = import.meta.env.VITE_ADMIN_URL as string | undefined
  const logout = () => {
    tokenStorage.clear()
    window.location.assign('/login')
  }
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="font-display text-2xl font-semibold">Platform account</p>
      <p className="max-w-md text-sm text-muted-foreground">
        This is the tenant portal. Your account is a DistroOps platform/admin account and has no
        company workspace here. Please use the admin console.
      </p>
      <div className="flex gap-3 pt-1">
        {adminUrl ? (
          <a href={adminUrl} className="rounded-md bg-brand-terracotta px-4 py-2 text-sm font-semibold text-white">
            Go to admin console
          </a>
        ) : null}
        <button onClick={logout} className="rounded-md border px-4 py-2 text-sm font-semibold">
          Log out
        </button>
      </div>
    </div>
  )
}

interface RequireAccessProps {
  permission?: string | string[]
  module?: string | string[]
  /** Passes if ANY one of these modules is enabled (e.g. gateway_* family). */
  moduleAny?: string[]
  requireAll?: boolean
}

/**
 * Gate for module/permission-specific routes. Renders a 403 view if the
 * user lacks access.
 */
export function RequireAccess({ permission, module, moduleAny, requireAll }: RequireAccessProps) {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const user = useAppSelector((s) => s.auth.user)
  const canAccess = useCanAccess({ permission, module, moduleAny, requireAll })

  // Right after login the token is set but /me (permissions + modules) may not
  // have arrived yet. Don't flash "Access denied" — wait for identity.
  if (isAuthenticated && !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-brand-terracotta" />
      </div>
    )
  }

  if (!canAccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="font-display text-2xl font-semibold">Access denied</p>
        <p className="max-w-md text-sm text-muted-foreground">
          You don't have permission to view this page, or the required module isn't
          enabled for your company. Contact your administrator if you think this is a
          mistake.
        </p>
      </div>
    )
  }
  return <Outlet />
}

/**
 * Gate for the DistroOps internal admin surface. Allows platform-level users
 * (super_admin / platform_staff) who hold the given platform.* permission.
 */
export function RequirePlatformAccess({ permission }: { permission?: string | string[] }) {
  const canPlatform = useCanPlatform(permission)
  if (!canPlatform) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="font-display text-2xl font-semibold">Access denied</p>
        <p className="max-w-md text-sm text-muted-foreground">
          This area is restricted to DistroOps platform staff with the right role.
        </p>
      </div>
    )
  }
  return <Outlet />
}
